import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Modal, Button, Row, Col, Spinner } from 'react-bootstrap';
import constants from '../../const';

const getFlatLayPath = (garment) => {
    if (garment.flatlay_url) return garment.flatlay_url;
    if (garment.image_url) return garment.image_url.replace('.webp', '_flatlay.webp');
    return null;
};

const handleFlatLayError = (garment) => (e) => {
    const fallback = garment.image_url ? `${constants.CONTENT_DOMAIN}${garment.image_url}` : null;
    if (fallback && e.currentTarget.src !== fallback) {
        e.currentTarget.src = fallback;
    }
};

function SceneGarmentViewer(props) {
    const { sceneId } = props;
    const [showGarmentViewer, setShowGarmentViewer] = useState(false);
    const [selectedGarment, setSelectedGarment] = useState(null);
    const [sceneGarments, setSceneGarments] = useState([]);
    const [isFetching, setIsFetching] = useState(false);

    const token = `?token=${localStorage.getItem('token').slice(7)}`;

    const fetchSceneGarments = async () => {
        setIsFetching(true);
        try {
            const response = await axios.get(
                `${constants.GARMENT_API.SCENE_GARMENT_TAG_URL}/${sceneId}`,
                {
                    headers: {
                        Authorization: `${localStorage.getItem('token')}`
                    }
                }
            );
            
            // Ensure response is an array
            let garments = [];
            if (Array.isArray(response.data)) {
                garments = response.data;
            } else if (response.data && Array.isArray(response.data.garments)) {
                garments = response.data.garments;
            } else if (response.data && Array.isArray(response.data.data)) {
                garments = response.data.data;
            }
            
            setSceneGarments(garments);
        } catch (error) {
            console.error('Error fetching scene garments:', error);
            setSceneGarments([]);
        } finally {
            setIsFetching(false);
        }
    };

    const handleShow = () => {
        setShowGarmentViewer(true);
        fetchSceneGarments();
    };

    const handleClose = () => {
        setShowGarmentViewer(false);
        setSelectedGarment(null);
    };

    if (!sceneId) {
        return null;
    }

    return (
        <>
            <ViewerButton onClick={handleShow}>
                👗 View Wardrobe ({Array.isArray(sceneGarments) ? sceneGarments.length : 0})
            </ViewerButton>

            <ModernModal
                show={showGarmentViewer}
                onHide={handleClose}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Scene Wardrobe ({Array.isArray(sceneGarments) ? sceneGarments.length : 0} garment(s))</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isFetching ? (
                        <LoadingContainer>
                            <Spinner animation="border" />
                        </LoadingContainer>
                    ) : (
                        <>
                            {selectedGarment ? (
                                <GarmentDetailView>
                                    <BackButton onClick={() => setSelectedGarment(null)}>
                                        ← Back to List
                                    </BackButton>
                                    <GarmentDetail garment={selectedGarment} />
                                </GarmentDetailView>
                            ) : Array.isArray(sceneGarments) && sceneGarments.length > 0 ? (
                                <GarmentGrid>
                                    {sceneGarments.map(garment => (
                                        <GarmentCard
                                            key={garment.id}
                                            onClick={() => setSelectedGarment(garment)}
                                        >
                                            {getFlatLayPath(garment) && (
                                                <GarmentImageContainer>
                                                    <GarmentImage
                                                        src={`${constants.CONTENT_DOMAIN}${getFlatLayPath(garment)}`}
                                                        alt={garment.name}
                                                        onError={handleFlatLayError(garment)}
                                                    />
                                                </GarmentImageContainer>
                                            )}
                                            <GarmentCardContent>
                                                <GarmentCardName>{garment.name}</GarmentCardName>
                                                {garment.color && (
                                                    <GarmentCardMeta>
                                                        {garment.color}
                                                    </GarmentCardMeta>
                                                )}
                                            </GarmentCardContent>
                                        </GarmentCard>
                                    ))}
                                </GarmentGrid>
                            ) : (
                                <div style={{ textAlign: 'center', color: '#999', padding: '40px 20px' }}>
                                    <p>No garments assigned to this scene</p>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
            </ModernModal>
        </>
    );
}

function GarmentDetail({ garment }) {
    return (
        <DetailContainer>
            {getFlatLayPath(garment) && (
                <DetailImageContainer>
                    <DetailImage
                        src={`${constants.CONTENT_DOMAIN}${getFlatLayPath(garment)}`}
                        alt={garment.name}
                        onError={handleFlatLayError(garment)}
                    />
                </DetailImageContainer>
            )}
            <DetailContent>
                <DetailTitle>{garment.name}</DetailTitle>

                <DetailSection>
                    <DetailSectionTitle>Properties</DetailSectionTitle>
                    <DetailGrid>
                        {garment.color && (
                            <DetailItem>
                                <DetailLabel>Color:</DetailLabel>
                                <DetailValue>
                                    <ColorBadge color={garment.color}>
                                        {garment.color}
                                    </ColorBadge>
                                </DetailValue>
                            </DetailItem>
                        )}
                        {garment.category_name && (
                            <DetailItem>
                                <DetailLabel>Category:</DetailLabel>
                                <DetailValue>{garment.category_name}</DetailValue>
                            </DetailItem>
                        )}
                        {garment.style_name && (
                            <DetailItem>
                                <DetailLabel>Style:</DetailLabel>
                                <DetailValue>{garment.style_name}</DetailValue>
                            </DetailItem>
                        )}
                        {garment.pattern_name && (
                            <DetailItem>
                                <DetailLabel>Pattern:</DetailLabel>
                                <DetailValue>{garment.pattern_name}</DetailValue>
                            </DetailItem>
                        )}
                        {garment.texture_name && (
                            <DetailItem>
                                <DetailLabel>Texture:</DetailLabel>
                                <DetailValue>{garment.texture_name}</DetailValue>
                            </DetailItem>
                        )}
                        {garment.material_name && (
                            <DetailItem>
                                <DetailLabel>Material:</DetailLabel>
                                <DetailValue>{garment.material_name}</DetailValue>
                            </DetailItem>
                        )}
                        {garment.cut && (
                            <DetailItem>
                                <DetailLabel>Cut:</DetailLabel>
                                <DetailValue>{garment.cut}</DetailValue>
                            </DetailItem>
                        )}
                        {garment.straps && (
                            <DetailItem>
                                <DetailLabel>Straps:</DetailLabel>
                                <DetailValue>{garment.straps}</DetailValue>
                            </DetailItem>
                        )}
                    </DetailGrid>
                </DetailSection>

                {garment.additional_attributes && (
                    <DetailSection>
                        <DetailSectionTitle>Additional Attributes</DetailSectionTitle>
                        <DetailText>{garment.additional_attributes}</DetailText>
                    </DetailSection>
                )}

                {garment.description && (
                    <DetailSection>
                        <DetailSectionTitle>Description</DetailSectionTitle>
                        <DetailText>{garment.description}</DetailText>
                    </DetailSection>
                )}
            </DetailContent>
        </DetailContainer>
    );
}

// STYLED COMPONENTS
const ViewerButton = styled(Button)`
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 16px rgba(102, 126, 234, 0.3);
    }

    &:active {
        transform: translateY(0);
    }
`;

const ModernModal = styled(Modal)`
    .modal-content {
        background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        border: none;
        border-radius: 12px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        overflow: hidden;
    }

    .modal-header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        padding: 16px 20px;

        .modal-title {
            font-weight: 700;
            font-size: 16px;
            color: white;
        }

        .btn-close {
            filter: brightness(0) invert(1);
        }
    }

    .modal-body {
        background: #f8f9fb;
        padding: 20px;
        max-height: 75vh;
        overflow-y: auto;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
`;

const GarmentGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 16px;
    padding: 8px;
`;

const GarmentCard = styled.div`
    background: white;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 16px rgba(102, 126, 234, 0.2);
    }
`;

const GarmentImageContainer = styled.div`
    width: 100%;
    height: 150px;
    overflow: hidden;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const GarmentImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const GarmentCardContent = styled.div`
    padding: 12px;
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const GarmentCardName = styled.h6`
    margin: 0 0 6px 0;
    font-weight: 600;
    color: #333;
    font-size: 13px;
    word-break: break-word;
`;

const GarmentCardMeta = styled.span`
    font-size: 11px;
    color: #666;
`;

const GarmentDetailView = styled.div``;

const BackButton = styled(Button)`
    margin-bottom: 16px;
    background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-weight: 600;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
    }
`;

const DetailContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: 24px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const DetailImageContainer = styled.div`
    border-radius: 8px;
    overflow: hidden;
    background: #f0f0f0;
    height: fit-content;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const DetailImage = styled.img`
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
`;

const DetailContent = styled.div``;

const DetailTitle = styled.h4`
    font-weight: 700;
    color: #333;
    margin-bottom: 20px;
    font-size: 20px;
`;

const DetailSection = styled.div`
    margin-bottom: 20px;
`;

const DetailSectionTitle = styled.h6`
    font-weight: 700;
    color: #667eea;
    margin-bottom: 12px;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

const DetailItem = styled.div`
    background: white;
    padding: 12px;
    border-radius: 6px;
    border-left: 4px solid #667eea;
`;

const DetailLabel = styled.span`
    display: block;
    font-weight: 600;
    color: #333;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
`;

const DetailValue = styled.span`
    display: block;
    color: #555;
    font-size: 14px;
`;

const DetailText = styled.p`
    background: white;
    padding: 12px;
    border-radius: 6px;
    border-left: 4px solid #667eea;
    margin: 0;
    color: #555;
    font-size: 14px;
    line-height: 1.6;
`;

const ColorBadge = styled.span`
    display: inline-block;
    padding: 6px 12px;
    border-radius: 20px;
    background: ${props => props.color || '#ccc'};
    color: ${props => getContrast(props.color) ? 'white' : 'black'};
    font-weight: 600;
    font-size: 12px;
`;

function getContrast(color) {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness < 128;
}

const mapStateToProps = (state) => {
    return {
        isFetching: state.garment ? state.garment.isFetching : false
    };
};

export default SceneGarmentViewer;
